/* The ArabCap Premiere Pro extension: where to get it and who can run it.
 *
 * Download links go through the proxy, which always redirects to the newest
 * signed installer (Backend/Proxy/app/routers/extension.py), so nothing here
 * changes when a new version ships. Always the production proxy: installers
 * only exist there, even when the app runs against a local API.
 *
 * The click also tells the proxy to send Meta the PremiereExtensionClick
 * event (the one the Premiere ads optimize on). The pixel fire below uses
 * the same event id, so Meta counts the download once. */

import { getAttribution } from '../../utils/attribution';

const API_BASE = 'https://arabcap-proxy.fly.dev';

export const PREMIERE_DOWNLOAD_URL = {
  mac: `${API_BASE}/api/extension/premiere/download/mac`,
  win: `${API_BASE}/api/extension/premiere/download/win`,
};

export const PREMIERE_PAGE_URL = 'https://arabcap.co/premiere-pro';

export const PLATFORM_LABEL = { mac: 'macOS', win: 'Windows' };

/* 'mac' | 'win', or null on phones, tablets and anything Premiere does not run on. */
export const detectPlatform = () => {
  if (typeof navigator === 'undefined') return null;
  const data = navigator.userAgentData;
  if (data?.mobile) return null;
  const hint = `${data?.platform || ''} ${navigator.platform || ''} ${navigator.userAgent || ''}`;
  if (/iPhone|iPad|iPod|Android/i.test(hint)) return null;
  // iPadOS reports itself as a Mac; a touch screen gives it away.
  if (/Mac/i.test(hint)) return navigator.maxTouchPoints > 1 ? null : 'mac';
  if (/Win/i.test(hint)) return 'win';
  return null;
};

const readCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
};

/* Download URL plus the event id shared with the server-sent Meta event. */
export const premiereDownloadLink = (platform, placement) => {
  const eventId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`).slice(0, 80);
  const url = new URL(PREMIERE_DOWNLOAD_URL[platform]);
  url.searchParams.set('eid', eventId);
  url.searchParams.set('placement', placement);
  try {
    const attr = getAttribution() || {};
    if (attr.fbclid) url.searchParams.set('fbclid', String(attr.fbclid).slice(0, 200));
    if (attr.first_seen_at) url.searchParams.set('seen', String(attr.first_seen_at).slice(0, 40));
  } catch { /* attribution is optional */ }
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');
  if (fbp) url.searchParams.set('fbp', fbp.slice(0, 120));
  if (fbc) url.searchParams.set('fbc', fbc.slice(0, 250));
  url.searchParams.set('source', `${window.location.origin}${window.location.pathname}`.slice(0, 500));
  return { href: url.toString(), eventId };
};

export const trackPremiereClick = (action, placement, platform) => {
  try {
    window.gtag?.('event', 'premiere_extension_click', { action, placement, platform });
  } catch {
    // analytics must never break the page
  }
};

/* Download uses one event id for the pixel and the server, so the ad counts it once. */
export const trackPremiereDownload = (placement, platform, eventId) => {
  try {
    window.gtag?.('event', 'premiere_extension_click', { action: 'download', placement, platform });
    window.fbq?.('trackCustom', 'PremiereExtensionClick', {
      action: 'download',
      placement,
      platform,
    }, { eventID: eventId });
  } catch {
    // analytics must never break the page
  }
};
